import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  LinearProgress,
  CircularProgress,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material"
import anime from "animejs"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"

import { useAuthUser } from "react-auth-kit"
import { useQuery } from "react-query"
import api from "../../utils/api"

interface Question {
  id: string
  description: string
  questionType: "Single" | "Ranked"
}

interface Answer {
  id: string
  questionId: string
  option: string
}

function OptionsBox({
  currentAnswers,
  activeButton,
  handleOptionClick,
  theme,
}: Readonly<{
  currentAnswers: Answer[]
  activeButton: string | null
  handleOptionClick: (answerId: string, questionId: string) => void
  theme: any
}>) {
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const animateButton = (option: string, index: number, scaleValue: number) => {
    const element = buttonRefs.current.get(`${index}-${option}`)
    if (element) {
      anime({
        targets: element,
        scale: scaleValue,
        duration: 300,
        easing: "easeOutQuad",
      })
    }
  }

  return (
    <Box p={2} display="flex" flexDirection="column" alignItems="center">
      {currentAnswers.map((answer, index) => (
        <Button
          key={`${answer.questionId}-${answer.id}`}
          ref={(el) =>
            el && buttonRefs.current.set(`${index}-${answer.option}`, el)
          }
          variant={activeButton === answer.id ? "contained" : "outlined"}
          color={activeButton === answer.id ? "primary" : "inherit"}
          onMouseEnter={() => animateButton(answer.option, index, 1.1)}
          onMouseLeave={() => animateButton(answer.option, index, 1)}
          onClick={() => handleOptionClick(answer.id, answer.questionId)}
          style={{
            margin: theme.spacing(1),
            width: "250px",
            backgroundColor: activeButton === answer.id ? "#FFA500" : "#e0e0e0",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            color: "black",
            fontSize: "0.875rem",
          }}
        >
          {answer.option}
        </Button>
      ))}
    </Box>
  )
}

function RankedOptionsBox({
  currentAnswers,
  handleOptionClick,
  questions,
  currentQuestionIndex,
  voterAnswers,
}: Readonly<{
  currentAnswers: Answer[]
  handleOptionClick: (
    answerId: string,
    rank: number,
    questionId: string
  ) => void
  questions: Question[]
  currentQuestionIndex: number
  voterAnswers: Record<string, string[]>
}>) {
  return (
    <Box p={2}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Options</TableCell>
            {currentAnswers.map((_, idx) => (
              <TableCell key={idx}>{idx + 1}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {currentAnswers.map((answer) => (
            <TableRow key={answer.id}>
              <TableCell>{answer.option}</TableCell>
              {currentAnswers.map((_, idx) => (
                <TableCell key={`${answer.id}-${idx}`}>
                  <Checkbox
                    checked={
                      voterAnswers[questions[currentQuestionIndex]?.id]?.[
                        idx
                      ] === answer.id
                    }
                    onChange={() =>
                      handleOptionClick(
                        answer.id,
                        idx,
                        questions[currentQuestionIndex]?.id
                      )
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

function NavigationButtons({
  currentQuestionIndex,
  questions,
  activeButton,
  updateQuestion,
  handleFinish,
  currentAnswers,
  voterAnswers,
}: Readonly<{
  currentQuestionIndex: number
  questions: Question[]
  activeButton: string | null
  updateQuestion: (delta: number) => void
  handleFinish: () => Promise<void>
  currentAnswers?: Answer[]
  voterAnswers?: Record<string, string[]>
}>) {
  const requiredAnswerCount = currentAnswers?.length

  const currentSelectedAnswers = voterAnswers
    ? voterAnswers[questions[currentQuestionIndex]?.id] || []
    : []

  const currentAnswerCount = currentSelectedAnswers.filter(
    (answer) => answer !== ""
  ).length

  // Logic for determining button activation
  const isSingleType =
    questions[currentQuestionIndex]?.questionType === "Single"
  const isSelectionValid = isSingleType
    ? !!activeButton
    : currentAnswerCount === requiredAnswerCount

  return (
    <Box
      p={2}
      display="flex"
      justifyContent={currentQuestionIndex !== 0 ? "space-between" : "flex-end"}
    >
      {currentQuestionIndex !== 0 && (
        <Button
          onClick={() => updateQuestion(-1)}
          variant="contained"
          color="primary"
          startIcon={<ChevronLeftIcon />}
        >
          Back
        </Button>
      )}
      {currentQuestionIndex < questions.length - 1 ? (
        <Button
          onClick={() => updateQuestion(1)}
          variant="contained"
          color="primary"
          endIcon={<ChevronRightIcon />}
          disabled={!isSelectionValid} // Use isSelectionValid for disabling/enabling
        >
          Next
        </Button>
      ) : (
        <Button
          onClick={handleFinish}
          variant="contained"
          color="error"
          endIcon={<ChevronRightIcon />}
          disabled={!isSelectionValid} // Use isSelectionValid for disabling/enabling
        >
          Finish
        </Button>
      )}
    </Box>
  )
}

function LoadQuestionsPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [activeButton, setActiveButton] = useState<string | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [answers, setAnswers] = useState<Answer[]>([])
  const [voterAnswers, setVoterAnswers] = useState<Record<string, string[]>>({})
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"))
  const appBarHeight = isMobile ? 56 : 64
  const auth = useAuthUser()
  const voterId = auth()?.id
  const eventId = auth()?.eventId
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")


  useEffect(() => {
    api
      .post("/voter/save-joined", { voterId})
  })

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20does%20not%20exist."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })

      api
        .get(`/admin/instances/${id}/status`)
        .then((response) => {
          const status = response.data.status
          if (status !== "open") {
            navigate("/not-authorized?message=Voting%20has%20not%20started.")
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])

  const fetchInstanceDetails = async (): Promise<any> => {
    const response = await api.get(`/admin/instances/${id}/details`)
    return response.data
  }

  const { data: instanceData } = useQuery(
    ["instanceDetails", id],
    fetchInstanceDetails,
    {
      refetchOnWindowFocus: false,
      onSuccess: () => setIsLoading(false),
      onError: () => setIsLoading(false),
    }
  )

  useEffect(() => {
    if (instanceData) {
      if (instanceData.questions && Array.isArray(instanceData.questions)) {
        // Sort the questions based on created at time
        const questionData = instanceData.questions
        const sortedQuestions = questionData.sort((a: any, b: any) => {
          const aSeconds = a.createdAt?._seconds || 0
          const bSeconds = b.createdAt?._seconds || 0
          return aSeconds - bSeconds
        })

        setQuestions(sortedQuestions)
      }
      // setQuestions(instanceData.questions)
      setAnswers(instanceData.answers)
    }
  }, [instanceData])

  const currentAnswers = useMemo(() => {
    return answers.filter(
      (answer) => answer.questionId === questions[currentQuestionIndex]?.id
    )
  }, [answers, questions, currentQuestionIndex])

  useEffect(() => {
    setActiveButton(
      voterAnswers[questions[currentQuestionIndex]?.id]?.[0] || null
    )
  }, [currentQuestionIndex, questions, voterAnswers])

  const updateQuestion = (delta: number) => {
    const newIndex = currentQuestionIndex + delta
    if (newIndex >= 0 && newIndex < questions.length) {
      setCurrentQuestionIndex(newIndex)
    }
  }

  const handleSingleOptionClick = (answerId: string, questionId: string) => {
    setVoterAnswers((prev) => ({ ...prev, [questionId]: [answerId] }))
  }

  const handleOptionClick = (
    answerId: string,
    rank: number,
    questionId: string
  ) => {
    setVoterAnswers((prev) => {
      let updatedAnswers = [...(prev[questionId] || [])]

      // Check if this answerId is already ranked somewhere else
      const existingRank = updatedAnswers.indexOf(answerId)
      if (existingRank > -1) {
        updatedAnswers[existingRank] = ""
      }

      updatedAnswers[rank] = answerId

      return { ...prev, [questionId]: updatedAnswers }
    })
  }

  const handleFinish = async () => {
    if (currentQuestionIndex === questions.length - 1 && activeButton) {
      try {
        const answersArray = Object.keys(voterAnswers).map((questionId) => ({
          questionId,
          answerId: voterAnswers[questionId],
        }))

        const response = await api.post("/voter/save-votes", {
          voterId,
          instanceId: id,
          answers: answersArray,
        })

        if (response.status === 200) {
          setErrorMessage("Voter results will be published soon.")
          setOpenSnackbar(true)
          setTimeout(() => {
            navigate(`/voter/dashboard`)
          }, 3000)
        } else {
          handleErrorResponse(response)
        }
      } catch (error) {
        if (error.response) {
          // Handle HTTP error response from Axios
          const { status, data } = error.response
          if (
            status === 403 &&
            data.message === "You have already voted for this instance."
          ) {
            setErrorMessage("You have already voted for this instance.")
            setOpenSnackbar(true)
          } else {
            console.error(
              "Failed to save votes:",
              data.message || "An unknown error occurred."
            )
          }
        } else {
          // Handle non-HTTP errors (network error, etc.)
          console.error(
            "Failed to save votes:",
            error.message || "An unknown network error occurred."
          )
        }
      }
    }
  }

  function handleErrorResponse(response: any) {
    const { status, data } = response
    if (
      status === 403 &&
      data.message === "You have already voted for this instance."
    ) {
      setErrorMessage("You have already voted for this instance.")
      setOpenSnackbar(true)
    } else {
      console.error(
        "Failed to save votes:",
        data.message || "An unknown error occurred."
      )
    }
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height={`calc(95vh - ${appBarHeight}px)`}
    >
      <Container maxWidth={!isMobile ? "md" : "sm"}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper elevation={2}>
                <Box p={2}>
                  <Typography variant="caption" display="block" gutterBottom>
                    <Typography variant="h6">
                      {currentQuestionIndex + 1}.{" "}
                      {questions[currentQuestionIndex]?.description}
                    </Typography>
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(currentQuestionIndex + 1) * (100 / questions.length)}
                />

                {questions[currentQuestionIndex]?.questionType === "Single" ? (
                  <OptionsBox
                    currentAnswers={currentAnswers}
                    activeButton={activeButton}
                    handleOptionClick={handleSingleOptionClick}
                    theme={theme}
                  />
                ) : (
                  <RankedOptionsBox
                    currentAnswers={currentAnswers}
                    handleOptionClick={handleOptionClick}
                    questions={questions}
                    currentQuestionIndex={currentQuestionIndex}
                    voterAnswers={voterAnswers}
                  />
                )}

                <NavigationButtons
                  currentQuestionIndex={currentQuestionIndex}
                  questions={questions}
                  activeButton={activeButton}
                  updateQuestion={updateQuestion}
                  handleFinish={handleFinish}
                  currentAnswers={
                    questions[currentQuestionIndex]?.questionType === "Ranked"
                      ? currentAnswers
                      : undefined
                  }
                  voterAnswers={
                    questions[currentQuestionIndex]?.questionType === "Ranked"
                      ? voterAnswers
                      : undefined
                  }
                />
              </Paper>
            </Grid>
          </Grid>
        )}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="info"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  )
}

export default LoadQuestionsPage
